import { useGetProductsPaginated } from "@/hooks/products/use-products";
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { Product } from "@/types/products/api-types/product-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const createProductsFilters = (columnFilters: MRT_ColumnFiltersState) => {
  const filters: {
    name?: string;
    category_name?: string;
    brand_name?: string;
  } = {};

  const getTrimmedString = (value: unknown) =>
    typeof value === "string" ? value.trim() : "";

  columnFilters.forEach((filter) => {
    const colName = filter.id.split(".")[1] ?? filter.id;
    const trimmedValue = getTrimmedString(filter.value);
    if (trimmedValue === "") return;

    if (colName === "name") {
      filters.name = trimmedValue;
    }
    if (colName === "category_name") {
      filters.category_name = trimmedValue;
    }
    if (colName === "brand_name") {
      filters.brand_name = trimmedValue;
    }
  });

  return filters;
};

interface ProductsTableProps {
  onProductClick: (product: Product) => void;
  onDeleteClick: (product: Product) => void;
}

const ProductsTable = ({
  onProductClick,
  onDeleteClick,
}: ProductsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<Product>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row, renderedCellValue }) => {
          const product = row.original;
          return (
            <button
              onClick={() => onProductClick?.(product)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        accessorKey: "category_name",
        header: "Category",
      },
      {
        accessorKey: "brand_name",
        header: "Brand",
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const product = row.original;
          return (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onDeleteClick(product)}
            >
              <Trash2 />
            </Button>
          );
        },
      },
    ],
    [onProductClick, onDeleteClick]
  );

  return (
    <>
      <APIPaginatedTable
        columns={columns}
        reactQuery={useGetProductsPaginated}
        reactQueryFilters={(state: APIPaginatedTableState) => {
          const filters = createProductsFilters(state.columnFilters);

          return {
            page: state.pagination.pageIndex,
            size: state.pagination.pageSize,
            ...filters,
          };
        }}
        onRenderToolbarInternalActions={({ table }) => (
          <>
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        titleTopic="products"
        contentTopic="product"
      />
    </>
  );
};

export default ProductsTable;
